import React, { useState, useRef } from 'react';
import { compose } from 'ramda';
import { RouteComponentProps } from 'react-router-dom';
import queryString from 'query-string';
import styled from 'styled-components/macro';

import { ProjectType } from 'store/modules/projects/projects.types';
import { OrganizationType } from 'store/modules/auth/auth';
import Forms from 'components/Forms';
import RouterProvider from 'components/RouterProvider/RouterProvider';
import ForAdminOnly from 'components/ForAdminOnly/ForAdminOnly';
import { useFormatMessage } from 'components/IntlProvider';
import api from 'api/api';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg';
import { ReactComponent as AddUsersIcon } from 'assets/icons/add-users.svg';
import { ReactComponent as ArchiveIcon } from 'assets/icons/archive.svg';
import { ProjectStatus } from 'consts/statuses';

import {
  ProjectActionButton,
  Typography,
  Modal,
  ModalTitle,
  ModalActions,
  ModalContent,
  Button,
  showToast,
} from '@deeptrue-frontend/ui';

const { connect, actions, selectors } = StoreProvider;
const { withRouter } = RouterProvider;

const ActionTypography = styled(Typography)<{ color: string }>`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ color, theme }) => theme.colors[color]};
`;

interface ProjectAdminActionsProps extends RouteComponentProps<any> {
  id: number;
  userOrganization: OrganizationType;
  projectName: string;
  status: number;
  setOrganizationProjects: (project: ProjectType) => void;
  setUserProjects: (project: ProjectType) => void;
}

const ProjectAdminActions = ({
  userOrganization,
  setUserProjects,
  id,
  setOrganizationProjects,
  match: { params },
  location,
  projectName,
  status,
}: ProjectAdminActionsProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isAddUserModalVisible, setUserModalVisible] = useState(false);
  const [isRemoveModalVisible, setRemoveModalVisible] = useState(false);
  const [isArchiveModalVisible, setArchiveModalVisible] = useState(false);
  const [isUnarchiveModalVisible, setUnarchiveModalVisible] = useState(false);
  const t = useFormatMessage();
  const parsedSearch = queryString.parse(location.search);
  const portalRef = useRef();
  const invitePortalRef = useRef();
  const archivePortalRef = useRef();
  const unarchivePortalRef = useRef();

  const refreshProjectList = () => {
    if (params.codename) {
      return api.resources
        .getOrganizationProjects({
          organization_name: params.codename,
          params: parsedSearch as { limit: string; offset: string },
        })
        .then(({ data }) => {
          setOrganizationProjects(data);
        });
    }

    return api.resources
      .userProjects({ organizationName: userOrganization.codename })
      .then(({ data }) => {
        setUserProjects(data);
      });
  };

  const removeProject = () => {
    setIsLoading(true);
    return api.resources
      .removeProject({
        organizationCodename: params.codename || userOrganization.codename,
        id,
      })
      .then(() => {
        showToast({
          type: 'success',
          title: t(
            'success.toast.project.remove',
            'Project successfully removed'
          ),
        });
      })
      .catch(() => {
        showToast({
          type: 'error',
          title: t(
            'errors.toast.general',
            'Something went wrong, please contact administrator'
          ),
        });
      })
      .finally(() => {
        refreshProjectList().finally(() => {
          setIsLoading(false);
        });
      });
  };

  const archiveProject = () => {
    setIsLoading(true);
    return api.resources
      .archiveProject(params.codename || userOrganization.codename, id)
      .then(() => {
        showToast({
          type: 'success',
          title: t(
            'success.toast.project.archive',
            'Project successfully archived'
          ),
        });
      })
      .catch(() => {
        showToast({
          type: 'error',
          title: t(
            'errors.toast.general',
            'Something went wrong, please contact administrator'
          ),
        });
      })
      .finally(() => {
        setArchiveModalVisible(false);
        refreshProjectList().finally(() => {
          setIsLoading(false);
        });
      });
  };

  const unarchiveProject = () => {
    setIsLoading(true);
    return api.resources
      .unarchiveProject(params.codename || userOrganization.codename, id)
      .then(() => {
        showToast({
          type: 'success',
          title: t(
            'success.toast.project.archive',
            'Project successfully archived'
          ),
        });
      })
      .catch(() => {
        showToast({
          type: 'error',
          title: t(
            'errors.toast.general',
            'Something went wrong, please contact administrator'
          ),
        });
      })
      .finally(() => {
        setUnarchiveModalVisible(false);
        refreshProjectList().finally(() => {
          setIsLoading(false);
        });
      });
  };

  if (isLoading) {
    return null;
  }

  return (
    <>
      <ProjectActionButton
        icon={<TrashIcon />}
        onClick={() => setRemoveModalVisible(true)}
        innerRef={portalRef}
      >
        <ActionTypography color="scaryRed">
          {t('tooltip.project.delete', 'Delete')}
        </ActionTypography>
      </ProjectActionButton>

      {status !== ProjectStatus.OFFER_ARCHIVED && (
        <ProjectActionButton
          icon={<ArchiveIcon />}
          onClick={() => setArchiveModalVisible(true)}
          innerRef={archivePortalRef}
        >
          <ActionTypography color="tuatara">
            {t('tooltip.project.archive', 'Archive')}
          </ActionTypography>
        </ProjectActionButton>
      )}

      {status === ProjectStatus.OFFER_ARCHIVED && (
        <ProjectActionButton
          icon={<ArchiveIcon />}
          onClick={() => setUnarchiveModalVisible(true)}
          innerRef={unarchivePortalRef}
        >
          <ActionTypography color="tuatara">
            {t('tooltip.project.unarchive', 'Unarchive')}
          </ActionTypography>
        </ProjectActionButton>
      )}

      <ForAdminOnly>
        <ProjectActionButton
          innerRef={invitePortalRef}
          icon={<AddUsersIcon />}
          onClick={() => setUserModalVisible(true)}
        >
          <ActionTypography color="tuatara">
            {t('tooltip.project.invite', 'Invite Users')}
          </ActionTypography>
        </ProjectActionButton>

        <Forms.AddUserModal
          anchor={portalRef}
          orgToInvite={params.codename || userOrganization.codename}
          isVisible={isAddUserModalVisible}
          onClose={() => setUserModalVisible(false)}
        />
      </ForAdminOnly>

      <Modal
        isVisible={isArchiveModalVisible}
        onBackdropClick={() => setArchiveModalVisible(false)}
        onCloseClick={() => setArchiveModalVisible(false)}
        portalAnchor={archivePortalRef}
      >
        <ModalTitle>
          {t('pages.workbenches.modal.archive', 'Archive project')}
        </ModalTitle>
        <ModalContent>
          {t(
            'pages.workbenches.modal.archiveAdd',
            'Are you sure you want to archive { name } project?',
            {
              name: projectName,
            }
          )}
        </ModalContent>
        <ModalActions justifyContent="space-between">
          <Button variant="danger" onClick={archiveProject}>
            {t('tooltip.workbenches.archive', 'Archive')}
          </Button>
          <Button
            variant="primary"
            onClick={() => setArchiveModalVisible(false)}
          >
            {t('pages.project.modal.cancel', 'Cancel')}
          </Button>
        </ModalActions>
      </Modal>
      <Modal
        isVisible={isUnarchiveModalVisible}
        onBackdropClick={() => setUnarchiveModalVisible(false)}
        onCloseClick={() => setUnarchiveModalVisible(false)}
        portalAnchor={unarchivePortalRef}
      >
        <ModalTitle>
          {t('pages.workbenches.modal.unarchive', 'Unarchive project')}
        </ModalTitle>
        <ModalContent>
          {t(
            'pages.workbenches.modal.unarchiveAdd',
            'Are you sure you want to unarchive { name } project?',
            {
              name: projectName,
            }
          )}
        </ModalContent>
        <ModalActions justifyContent="space-between">
          <Button variant="danger" onClick={unarchiveProject}>
            {t('tooltip.workbenches.unarchive', 'Unarchive')}
          </Button>
          <Button
            variant="primary"
            onClick={() => setArchiveModalVisible(false)}
          >
            {t('pages.project.modal.cancel', 'Cancel')}
          </Button>
        </ModalActions>
      </Modal>
      <Modal
        isVisible={isRemoveModalVisible}
        onBackdropClick={() => setRemoveModalVisible(false)}
        onCloseClick={() => setRemoveModalVisible(false)}
        portalAnchor={portalRef}
      >
        <ModalTitle>
          {t('pages.workbenches.modal.remove', 'Remove project')}
        </ModalTitle>
        <ModalContent>
          {t(
            'pages.workbenches.modal.removeAdd',
            'Are you sure you want to remove { name } project?',
            {
              name: projectName,
            }
          )}
        </ModalContent>
        <ModalActions justifyContent="space-between">
          <Button variant="danger" onClick={removeProject}>
            {t('tooltip.workbenches.remove', 'Remove')}
          </Button>
          <Button
            variant="primary"
            onClick={() => setRemoveModalVisible(false)}
          >
            {t('pages.project.modal.cancel', 'Cancel')}
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
};

export default compose(
  connect(
    (state) => ({
      userOrganization: selectors.authSelectors.userOrganization(state),
    }),
    {
      setOrganizationProjects:
        actions.organizationsActions.setOrganizationProjects,
      setUserProjects: actions.projects.setProjects,
    }
  ),
  withRouter
)(ProjectAdminActions);
